import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProgressIndicator from '../../components/ui/ProgressIndicator';
import AssessmentNavigation from '../../components/ui/AssessmentNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import OrganizationalStructure from './components/OrganizationalStructure';
import ControlSystems from './components/ControlSystems';
import ManagementCapacity from './components/ManagementCapacity';
import RiskIndicators from './components/RiskIndicators';

const GovernanceEvaluation = () => {
  const navigate = useNavigate();
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [structure, setStructure] = useState({
    positions: [
      {
        id: Date.now(),
        title: '',
        department: '',
        level: '',
        holder: '',
        experience: '',
        directReports: '',
        responsibilities: ''
      }
    ]
  });

  const [controls, setControls] = useState({
    financial: {},
    operational: {},
    quality: {},
    compliance: {}
  });

  const [capacity, setCapacity] = useState({
    technical: { level: '', skills: [], gaps: '' },
    managerial: { level: '', skills: [], gaps: '' },
    financial: { level: '', skills: [], gaps: '' },
    strategic: { level: '', skills: [], gaps: '' },
    overallReadiness: '',
    additionalPositions: '',
    developmentPlan: ''
  });

  const handleStructureChange = (positionId, field, value) => {
    setStructure(prev => ({
      ...prev,
      positions: prev?.positions?.map(pos =>
        pos?.id === positionId ? { ...pos, [field]: value } : pos
      )
    }));
  };

  const handleAddPosition = () => {
    setStructure(prev => ({
      ...prev,
      positions: [
        ...prev?.positions,
        {
          id: Date.now(),
          title: '',
          department: '',
          level: '',
          holder: '',
          experience: '',
          directReports: '',
          responsibilities: ''
        }
      ]
    }));
  };

  const handleRemovePosition = (positionId) => {
    setStructure(prev => ({
      ...prev,
      positions: prev?.positions?.filter(pos => pos?.id !== positionId)
    }));
  };

  const handleControlChange = (area, control, field, value) => {
    setControls(prev => ({
      ...prev,
      [area]: {
        ...prev?.[area],
        [control]: {
          ...prev?.[area]?.[control],
          [field]: value
        }
      }
    }));
  };

  const handleCapacityChange = (area, field, value) => {
    if (field === null) {
      setCapacity(prev => ({
        ...prev,
        [area]: value
      }));
    } else {
      setCapacity(prev => ({
        ...prev,
        [area]: {
          ...prev?.[area],
          [field]: value
        }
      }));
    }
  };

  const validateForm = () => {
    const hasValidStructure = structure?.positions?.some(pos =>
      pos?.title && pos?.department && pos?.level && pos?.holder
    );

    const hasControls = Object.values(controls)?.some(area =>
      Object.values(area)?.some(control => control?.implemented)
    );

    const hasCapacity = ['technical', 'managerial', 'financial', 'strategic']?.some(
      area => capacity?.[area]?.level
    );

    return hasValidStructure && hasControls && hasCapacity && capacity?.overallReadiness;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      alert('Mohon lengkapi minimal satu posisi organisasi, satu sistem kontrol, dan evaluasi kapasitas manajemen.');
      return;
    }

    setShowSuccessModal(true);
  };

  const handleContinue = () => {
    setShowSuccessModal(false);
    navigate('/ai-analysis-dashboard');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ProgressIndicator currentStep={7} completedSteps={[1, 2, 3, 4, 5, 6]} />
      <AssessmentNavigation completedModules={['supplier', 'operational', 'equipment', 'financial', 'market']} />

      <main className="with-assessment-nav pt-8 pb-16">
        <div className="container-executive">
          <div className="mb-8 md:mb-12">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Shield" size={28} color="var(--color-primary)" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3">
                  Evaluasi Tata Kelola dan Manajemen
                </h1>
                <p className="text-base md:text-lg text-muted-foreground">
                  Analisis struktur organisasi, sistem kontrol internal, dan kapasitas manajemen untuk identifikasi risiko tata kelola dan kesiapan scale-up
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-lg">
                <Icon name="Network" size={18} color="var(--color-primary)" />
                <span className="text-sm font-medium">Struktur Organisasi</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-lg">
                <Icon name="ShieldCheck" size={18} color="var(--color-primary)" />
                <span className="text-sm font-medium">Sistem Kontrol</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-lg">
                <Icon name="GraduationCap" size={18} color="var(--color-primary)" />
                <span className="text-sm font-medium">Kapasitas Manajemen</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2 space-y-6 lg:space-y-8">
              <OrganizationalStructure
                structure={structure}
                onStructureChange={handleStructureChange}
                onAddPosition={handleAddPosition}
                onRemovePosition={handleRemovePosition}
              />

              <ControlSystems
                controls={controls}
                onControlChange={handleControlChange}
              />

              <ManagementCapacity
                capacity={capacity}
                onCapacityChange={handleCapacityChange}
              />

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  variant="outline"
                  size="lg"
                  fullWidth
                  iconName="ArrowLeft"
                  iconPosition="left"
                  onClick={() => navigate('/market-assessment')}
                >
                  Kembali ke Penilaian Pasar
                </Button>
                <Button
                  variant="default"
                  size="lg"
                  fullWidth
                  iconName="Brain"
                  iconPosition="right"
                  onClick={handleSubmit}
                >
                  Lanjut ke Analisis AI
                </Button>
              </div>
            </div>

            <div className="lg:col-span-1">
              <RiskIndicators
                structure={structure}
                controls={controls}
                capacity={capacity}
              />
            </div>
          </div>
        </div>
      </main>

      {showSuccessModal && (
        <div className="fixed inset-0 z-[200] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-2xl max-w-md w-full p-6 md:p-8 border border-border">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-success/10 flex items-center justify-center mb-4">
                <Icon name="CheckCircle2" size={40} color="var(--color-success)" />
              </div>
              
              <h3 className="text-xl md:text-2xl font-bold mb-3">
                Evaluasi Tata Kelola Selesai!
              </h3>
              
              <p className="text-sm md:text-base text-muted-foreground mb-6">
                Data struktur organisasi, sistem kontrol, dan kapasitas manajemen telah tersimpan. Sistem AI siap menganalisis risiko tata kelola dan memberikan rekomendasi pengembangan organisasi.
              </p>

              <div className="w-full space-y-3">
                <Button
                  variant="default"
                  size="lg"
                  fullWidth
                  iconName="Brain"
                  iconPosition="right"
                  onClick={handleContinue}
                >
                  Mulai Analisis AI
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  fullWidth
                  onClick={() => setShowSuccessModal(false)}
                >
                  Tinjau Kembali
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GovernanceEvaluation;